/**
 * ASTM C1074 9.2 calibration check helpers.
 *
 * A pour must compute maturity with the same function (Nurse-Saul TTF or
 * Arrhenius equivalent age) and the same inputs (datum temperature, or
 * activation energy and specified temperature) as its calibration was
 * developed with. The server builds the check (CalibrationCheck); these
 * helpers pick the calibration a new pour may use and word the result.
 *
 * The rule holds a save only for a pour that computes maturity
 * (pourComputesMaturity). Every pour still gets the mix design's active,
 * finished calibration, and for any other pour the check is information.
 */
import type { Basis, BasisDifference, CalibrationCheck } from '../services/pours';
import type { ProductCalibrationLink } from '../services/products';
import type { MonitoringFunctionId } from '../components/pour/PourFunctionSelector';

/** Product2Calibration.Status of a finished (completed) calibration. */
export const CALIBRATION_STATUS_FINISHED = '2';

/** What a pour of a mix design without a maturity input group computes
 *  with: the server's defaults, on the Arrhenius method. */
export const DEFAULT_MATURITY_METHOD = 'Arrhenius';

/** Shown when the mix design has no active, finished calibration. */
export const NO_ACTIVE_CALIBRATION_TEXT =
  'No active finished calibration: targets will be tracked on maturity only.';

/** Shown when the mix design's active, finished calibration has no usable
 *  curve, so the server gives the pour none: the server's own words
 *  (ACTIVE_UNUSABLE_TEXT), used when its 'none' check sends no text. */
export const NO_USABLE_CURVE_TEXT =
  'The active calibration has no usable curve: targets will be tracked on maturity only.';

/** Added to a 'differs' or 'mismatch' check on a pour that does not compute
 *  maturity: the rule does not hold its save. */
export const INFORMATION_ONLY_TEXT = 'For information only: this pour does not compute maturity.';

/** Under a 'mismatch' that holds the save of a new pick. */
export const MISMATCH_ADVICE_TEXT =
  'Saving is disabled: choose a mix design whose calibration uses the same maturity function.';

/** Under a 'mismatch' that holds a save making the saved pour compute
 *  maturity, before its first reading (the mix design can still change). */
export const MISMATCH_ADVICE_ADDED_MATURITY_TEXT =
  'Saving is disabled: remove Concrete Maturity, or choose a mix design whose calibration uses the same maturity function.';

/** As above after the first reading: the mix design can no longer change. */
export const MISMATCH_ADVICE_ADDED_MATURITY_LOCKED_TEXT =
  'Saving is disabled: remove Concrete Maturity, or set up a new pour with a mix design whose calibration uses the same maturity function.';

/** Label of the checkbox that confirms a 'differs' check. */
export const ACKNOWLEDGE_DIFFERENCE_LABEL =
  'I understand the pour and the calibration use different maturity inputs';

/** The server's 422 when a calibration other than its own pick is sent. */
export const NOT_ACTIVE_CALIBRATION_TEXT = "Only the mix design's active, finished calibration can be used.";

/** What the server adds to a 'differs' refusal (422). */
export const CONFIRM_SUFFIX = ' Confirm to continue.';

/** A new calibration on the saved mix design, which has no maturity input
 *  group: the pour keeps its own stored inputs, which only the save can
 *  check (the check endpoint assumes the defaults). */
export const CHECKED_ON_SAVE_TEXT =
  'This mix design has no maturity input group, so the pour keeps its own maturity inputs. ' +
  'They are checked against the calibration (ASTM C1074 9.2) when you save.';

/**
 * The mix design's ACTIVE link to a FINISHED calibration, or null. There is
 * no fallback to another link.
 *
 * Only a hint: the server picks the calibration a pour gets (it also needs a
 * usable curve, and takes the newest of several), and the check
 * (POST /pour/calibration-check without a calibration) reports that pick.
 * No link here means the server has none either: maturity only.
 */
export const pickActiveFinishedCalibration = (
  links: ProductCalibrationLink[] | null | undefined,
): ProductCalibrationLink | null =>
  (links ?? []).find(
    (link) => link.is_active === true && String(link.status ?? '') === CALIBRATION_STATUS_FINISHED,
  ) ?? null;

/**
 * Why a planned pour gets no calibration, or null when it gets one (or the
 * check has not said yet): the mix design has no active, finished link, or
 * it has one but the server picked none (its 'none' check says why, e.g. the
 * curve is not usable).
 */
export const noCalibrationText = (
  links: ProductCalibrationLink[] | null | undefined,
  check: CalibrationCheck | null | undefined,
): string | null => {
  if (!pickActiveFinishedCalibration(links)) return NO_ACTIVE_CALIBRATION_TEXT;
  if (check?.status === 'none') return check.text || NO_USABLE_CURVE_TEXT;
  return null;
};

const DIFFERENCE_LABELS: Record<BasisDifference, string> = {
  method: 'Maturity method',
  datum_temp: 'Datum temperature',
  activation_energy: 'Activation energy',
  specified_temp: 'Specified temperature',
};

/** "Nurse-Saul TTF" | "Arrhenius equivalent age": only the exact method
 *  "Nurse Saul" is Nurse-Saul, as in the maturity engine. */
export const describeMethod = (method: string | null | undefined): string =>
  method === 'Nurse Saul' ? 'Nurse-Saul TTF' : 'Arrhenius equivalent age';

const describeValue = (difference: string, basis: Basis | null): string | null => {
  if (!basis) return null;
  switch (difference) {
    case 'method':
      return describeMethod(basis.method);
    case 'datum_temp':
      return basis.datum_temp != null ? `${basis.datum_temp} °C` : null;
    case 'specified_temp':
      return basis.specified_temp != null ? `${basis.specified_temp} °C` : null;
    case 'activation_energy':
      if (basis.activation_energy == null) return null;
      return basis.q_value != null
        ? `${basis.activation_energy} J/mol (Q ${basis.q_value} K)`
        : `${basis.activation_energy} J/mol`;
    default:
      return null;
  }
};

/**
 * The differences in plain words, one line each, e.g.
 * "Activation energy: this pour 39073 J/mol (Q 4702 K), the calibration
 * 40650 J/mol (Q 4892 K)".
 */
export const basisDifferenceLines = (check: CalibrationCheck | null | undefined): string[] =>
  (check?.differences ?? []).map((difference) => {
    const label = DIFFERENCE_LABELS[difference as BasisDifference] ?? difference;
    const pourValue = describeValue(difference, check?.pour_basis ?? null);
    const calValue = describeValue(difference, check?.calibration_basis ?? null);
    if (pourValue && calValue) {
      return `${label}: this pour ${pourValue}, the calibration ${calValue}`;
    }
    return label;
  });

/**
 * Why a save must wait for this check, or null when it may go ahead. The
 * server refuses the same cases (422) on a pour that computes maturity: a
 * different maturity function always, and different inputs until the user
 * confirms them. Callers ask only for such a pour.
 */
export const calibrationCheckBlock = (
  check: CalibrationCheck | null | undefined,
  acknowledged: boolean,
): string | null => {
  if (!check) return null;
  if (check.status === 'mismatch') return check.text;
  if (check.status === 'differs' && !acknowledged) return `${check.text}${CONFIRM_SUFFIX}`;
  return null;
};

/** The payload flag: sent only for a confirmed 'differs' check. */
export const acknowledgeFlag = (
  check: CalibrationCheck | null | undefined,
  acknowledged: boolean,
): true | undefined => (check?.status === 'differs' && acknowledged ? true : undefined);

export type CalibrationRefusal =
  | { status: 'differs' | 'mismatch'; text: string }
  | { status: 'not_active' };

/**
 * A save the server refused on the calibration rules, from its 422 detail:
 * 'differs' (the text without " Confirm to continue."), 'mismatch', or
 * 'not_active' (the calibration sent is not the server's pick). Null for any
 * other refusal.
 */
export const calibrationRefusal = (detail: string | null | undefined): CalibrationRefusal | null => {
  const text = (detail ?? '').trim();
  if (!text) return null;
  if (text === NOT_ACTIVE_CALIBRATION_TEXT) return { status: 'not_active' };
  if (!text.includes('ASTM C1074 9.2')) return null;
  if (text.endsWith(CONFIRM_SUFFIX.trim())) {
    return { status: 'differs', text: text.slice(0, -CONFIRM_SUFFIX.trim().length).trim() };
  }
  if (text.includes('requires the same maturity function')) return { status: 'mismatch', text };
  return null;
};

/** The monitoring function names the server recognises (PourFunctionSelector
 *  MonitoringFunctionId; backend utils/monitoring_access.parse_functions). */
const MONITORING_FUNCTION_NAMES: Record<MonitoringFunctionId, true> = {
  'Temperature Only': true,
  'Concrete Maturity': true,
  'Temperature Match Curing': true,
  'External Temperature and Humidity': true,
  'Onsite Weather Conditions': true,
  'Calculate Concrete Evaporation Rate': true,
};

/**
 * Whether a pour computes maturity, so ASTM C1074 9.2 holds its save and its
 * calibration basis matters: Concrete Maturity (Temperature Match Curing
 * includes it), or for a legacy pour without recorded functions the
 * functionality code '1' (maturity) or '3' (both), leading zeros ignored.
 * As the server's computes_maturity (utils/monitoring_access): names it does
 * not recognise are not recorded functions.
 */
export const pourComputesMaturity = (
  monitoringFunctions: string | null | undefined,
  functionality: string | null | undefined,
): boolean => {
  const fns = (monitoringFunctions ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => Object.prototype.hasOwnProperty.call(MONITORING_FUNCTION_NAMES, s));
  if (fns.length > 0) {
    return fns.includes('Concrete Maturity') || fns.includes('Temperature Match Curing');
  }
  const code = (functionality ?? '').trim().replace(/^0+/, '');
  return code === '1' || code === '3';
};
